import {
  forwardRef,
  useRef,
  useState,
  useCallback,
  useEffect,
  useImperativeHandle,
  type CSSProperties,
} from 'react';
import { FaPencilAlt } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import { HiX } from 'react-icons/hi';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import { BsGrid3X3 } from 'react-icons/bs';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Modal, ModalMethods, Text, Button, toasts, Loader } from '../../../../../ui-component';
import { api } from '../../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../../utils/common';
import { convertNumberOrStringToPriceFormat } from '../../../../../utils/common';
import SingleSelectOptionModal from './SingleSelectOptionModal';
import MultiSelectOptionModal from './MultiSelectOptionModal';
import NumberOptionModal from './NumberOptionModal';
import MultiLineOptionModal from './MultiLineOptionModal';

export type { StitchOptionType, StitchOptionValueApi, StitchOptionApi } from './optionsModalTypes';
import type { StitchOptionApi, StitchOptionType } from './optionsModalTypes';

const SELECT_INPUT_TYPE_OPTIONS: {
  type: StitchOptionType;
  label: string;
  icon: React.ReactNode;
}[] = [
  {
    type: 'RADIO',
    label: 'Single Select',
    icon: (
      <div className="w-10 h-10 rounded-lg border-2 border-gray-300 flex items-center justify-center">
        <BsGrid3X3 size={20} className="text-gray-600" />
      </div>
    ),
  },
  {
    type: 'MULTI_SELECT',
    label: 'Multi Select',
    icon: (
      <div className="w-10 h-10 rounded-lg border-2 border-gray-300 flex items-center justify-center">
        <BsGrid3X3 size={20} className="text-gray-600" />
      </div>
    ),
  },
  {
    type: 'COUNTER',
    label: 'Number',
    icon: (
      <div className="w-10 h-10 rounded-lg border-2 border-blue-500 flex items-center justify-center">
        <span className="text-blue-600 font-semibold text-sm">123</span>
      </div>
    ),
  },
  {
    type: 'MULTI_LINE_INPUT',
    label: 'Multi Line Text',
    icon: (
      <div className="w-10 h-10 rounded-lg border-2 border-blue-500 flex items-center justify-center">
        <span className="text-blue-600 font-semibold text-sm">ABC</span>
      </div>
    ),
  },
];

type SelectInputTypeModalProps = {
  onSelectType: (type: StitchOptionType) => void;
};

const SelectInputTypeModal = forwardRef<ModalMethods, SelectInputTypeModalProps>(
  ({ onSelectType }, ref) => {
    const modalRef = useRef<ModalMethods>(null);

    useImperativeHandle(ref, () => ({
      show: () => modalRef.current?.show(),
      hide: () => modalRef.current?.hide(),
      toggle: () => modalRef.current?.toggle(),
      getModalState: () => modalRef.current?.getModalState() ?? false,
    }));

    const headerComponent = (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => modalRef.current?.hide()}
          className="p-1 rounded hover:bg-gray-100"
          aria-label="Back"
        >
          <HiChevronLeft size={24} />
        </button>
        <span className="font-inter font-semibold text-lg">Select Input Type</span>
      </div>
    );

    return (
      <Modal
        ref={modalRef}
        size="xl"
        headerComponent={headerComponent}
        showFooter={false}
        showCloseIcon={false}
      >
        <div className="flex flex-col gap-2 py-2">
          {SELECT_INPUT_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.type}
              type="button"
              onClick={() => onSelectType(opt.type)}
              className="flex items-center gap-4 w-full p-4 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
            >
              {opt.icon}
              <span className="font-inter font-medium text-base">{opt.label}</span>
            </button>
          ))}
        </div>
      </Modal>
    );
  }
);

SelectInputTypeModal.displayName = 'SelectInputTypeModal';

type OutfitPiece = {
  id: number;
  name: string;
  outfit_link?: string;
};

type OptionsModalProps = {
  outfitName: string;
  outfitPieces: OutfitPiece[];
  selectedPieceId: number | null;
  onPieceChange?: (pieceId: number) => void;
  onOptionsUpdated?: () => void;
};

type SortableOptionItemProps = {
  option: StitchOptionApi;
  onModify: (option: StitchOptionApi) => void;
  onDelete: (optionId: number) => void;
};

const SortableOptionItem = ({ option, onModify, onDelete }: SortableOptionItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: option.id,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-col gap-2 p-4 rounded-lg bg-gray-50 border border-gray-200"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="p-1 rounded-full text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
            aria-label="Reorder option"
          >
            <BsGrid3X3 size={16} />
          </button>
          <p className="font-inter font-semibold text-sm" color="black">
            {option.key}
          </p>
          <Text className="font-inter text-xs text-gray-600" color="gray">
            {getOptionTypeLabel(option.type)}
          </Text>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onModify(option)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0 flex items-center gap-1"
            aria-label="Modify option"
          >
            <FaPencilAlt size={14} />
            <span className="text-sm font-medium">Modify</span>
          </button>
          <button
            type="button"
            onClick={() => onDelete(option.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0 flex items-center gap-1"
            aria-label="Delete option"
          >
            <MdDelete size={18} />
            <span className="text-sm font-medium">Delete</span>
          </button>
        </div>
      </div>
      {option.type === 'COUNTER' && (
        <Text className="font-inter text-xs text-gray-600" color="gray">
          Min {option.min_value ?? 0} • Max {option.max_value ?? 0} • Default{' '}
          {option.default_value ?? option.min_value ?? 0}
        </Text>
      )}
      {(option.type === 'RADIO' || option.type === 'MULTI_SELECT') && option.values.length > 0 && (
        <div className="flex flex-wrap gap-4 mt-2">
          {option.values.map((value, idx) => (
            <div key={idx} className="flex flex-col items-center w-32">
              <div className="relative w-full aspect-square rounded-2xl bg-white border border-gray-200 shadow-sm flex items-center justify-center">
                <div className="absolute top-2 right-2 w-4 h-4 border border-gray-400 rounded-sm bg-white" />
                {value.image_url ? (
                  <img
                    src={value.image_url}
                    alt={value.field_label}
                    className="w-16 h-16 object-contain rounded-sm"
                  />
                ) : (
                  <span className="w-14 h-14 rounded bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
                    +
                  </span>
                )}
                <div className="absolute bottom-3 left-0 right-0 flex justify-center">
                  <span className="font-inter text-sm font-semibold text-indigo-700">
                    +{convertNumberOrStringToPriceFormat(value.price)} Rs
                  </span>
                </div>
              </div>
              <span className="mt-2 font-inter text-sm text-center text-gray-900">
                {value.field_label}
              </span>
            </div>
          ))}
        </div>
      )}
      {option.type === 'MULTI_LINE_INPUT' && option.values?.[0] && (
        <div className="mt-1">
          <textarea
            readOnly
            className="w-full min-h-[80px] p-3 border border-gray-200 rounded-lg font-inter text-sm bg-gray-50"
            placeholder={option.values[0].field_label || 'You can enter any text here'}
          />
          {option.values[0].price !== undefined && option.values[0].price !== null && (
            <Text className="mt-1 font-inter text-xs text-indigo-700" color="gray">
              +{convertNumberOrStringToPriceFormat(option.values[0].price)} Rs
            </Text>
          )}
        </div>
      )}
    </div>
  );
};

const getOptionTypeLabel = (type: string) => {
  switch (type) {
    case 'RADIO':
      return 'Single Select';
    case 'MULTI_SELECT':
      return 'Multi Select';
    case 'COUNTER':
      return 'Input number field';
    case 'MULTI_LINE_INPUT':
      return 'Multi line text';
    default:
      return type;
  }
};

const OptionsModal = forwardRef<ModalMethods, OptionsModalProps>(
  ({ outfitName, outfitPieces, selectedPieceId, onPieceChange, onOptionsUpdated }, ref) => {
    const selectInputTypeModalRef = useRef<ModalMethods>(null);
    const singleSelectModalRef = useRef<ModalMethods>(null);
    const multiSelectModalRef = useRef<ModalMethods>(null);
    const numberModalRef = useRef<ModalMethods>(null);
    const multiLineModalRef = useRef<ModalMethods>(null);

    const [optionsData, setOptionsData] = useState<{ response: StitchOptionApi[] } | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [optionToEdit, setOptionToEdit] = useState<StitchOptionApi | null>(null);
    const [isReordering, setIsReordering] = useState(false);

    const currentPiece = outfitPieces.find((p) => p.id === selectedPieceId);

    const fetchOptions = useCallback(async () => {
      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id || !selectedPieceId) {
        setOptionsData(null);

        return;
      }

      setIsLoading(true);

      try {
        const response = await api.getRequest(
          `new-stitch-option/${boutique_id}/boutique_stitch_options?atomic_outfit_id=${selectedPieceId}`
        );

        if (response.status && response.data?.response) {
          setOptionsData({ response: response.data.response });
        } else {
          setOptionsData({ response: [] });
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'fetch-options-error');
        }
        setOptionsData({ response: [] });
      } finally {
        setIsLoading(false);
      }
    }, [selectedPieceId]);

    useEffect(() => {
      void fetchOptions();
    }, [fetchOptions]);

    const handleAddOption = () => {
      setOptionToEdit(null);
      selectInputTypeModalRef.current?.show();
    };

    const handleSelectInputType = (type: StitchOptionType) => {
      selectInputTypeModalRef.current?.hide();

      if (type === 'RADIO') {
        singleSelectModalRef.current?.show();
      } else if (type === 'MULTI_SELECT') {
        multiSelectModalRef.current?.show();
      } else if (type === 'COUNTER') {
        numberModalRef.current?.show();
      } else {
        multiLineModalRef.current?.show();
      }
    };

    const handleModifyOption = (option: StitchOptionApi) => {
      setOptionToEdit(option);

      if (option.type === 'RADIO') {
        singleSelectModalRef.current?.show();
      } else if (option.type === 'MULTI_SELECT') {
        multiSelectModalRef.current?.show();
      } else if (option.type === 'COUNTER') {
        numberModalRef.current?.show();
      } else {
        multiLineModalRef.current?.show();
      }
    };

    const handleDeleteOption = async (optionId: number) => {
      try {
        const response = await api.deleteRequest(`new-stitch-option/stitch-options/${optionId}`);

        if (response.status) {
          toasts('success', 'Option deleted successfully', 'option-delete-success');
          void fetchOptions();

          if (onOptionsUpdated) {
            onOptionsUpdated();
          }
        } else {
          toasts('error', response.message || 'Failed to delete option', 'option-delete-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'option-delete-error');
        }
      }
    };

    const handleNavigatePiece = (direction: 'prev' | 'next') => {
      const currentIndex = outfitPieces.findIndex((p) => p.id === selectedPieceId);

      if (currentIndex === -1) {
        return;
      }

      const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;

      if (newIndex >= 0 && newIndex < outfitPieces.length) {
        const newPiece = outfitPieces[newIndex];

        if (onPieceChange) {
          onPieceChange(newPiece.id);
        }
      }
    };

    const handleOptionSaved = () => {
      setOptionToEdit(null);
      void fetchOptions();

      if (onOptionsUpdated) {
        onOptionsUpdated();
      }
    };

    const persistOptionOrder = useCallback(
      async (orderedOptions: StitchOptionApi[]) => {
        const boutique_id = getValueFromLocalStorage('boutique_id');

        if (!boutique_id || !selectedPieceId) {
          return;
        }

        setIsReordering(true);

        try {
          const response = await api.putRequest(
            `new-stitch-option/${boutique_id}/boutique_stitch_options/swap?atomic_outfit_id=${selectedPieceId}`,
            orderedOptions.map((opt) => opt.id)
          );

          if (!response.status) {
            toasts(
              'error',
              response.message || 'Failed to update option order',
              'option-reorder-error'
            );
            void fetchOptions();
          } else if (onOptionsUpdated) {
            onOptionsUpdated();
          }
        } catch (err) {
          if (err instanceof Error) {
            toasts('error', err.message, 'option-reorder-error');
          }

          void fetchOptions();
        } finally {
          setIsReordering(false);
        }
      },
      [fetchOptions, onOptionsUpdated, selectedPieceId]
    );

    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
          return;
        }

        setOptionsData((prev) => {
          if (!prev) {
            return prev;
          }

          const oldIndex = prev.response.findIndex((item) => item.id === active.id);
          const newIndex = prev.response.findIndex((item) => item.id === over.id);

          if (oldIndex === -1 || newIndex === -1) {
            return prev;
          }

          const newResponse = arrayMove(prev.response, oldIndex, newIndex);

          void persistOptionOrder(newResponse);

          return { response: newResponse };
        });
      },
      [persistOptionOrder]
    );

    const options = optionsData?.response ?? [];

    const optionIds = options.map((item) => item.id);

    const isDndDisabled = isLoading || isReordering || options.length === 0;

    const dndWrapperClassName = isDndDisabled ? 'pointer-events-none opacity-60' : '';

    const headerHint =
      options.length > 0 && !isDndDisabled
        ? 'Drag the grid icon to re-arrange options'
        : options.length > 0
          ? 'Reordering disabled while loading'
          : '';

    const showHeaderHint = Boolean(headerHint);

    const headerComponent = (
      <div className="flex items-center justify-between w-full pr-8">
        <Text size="large" fontWeight={700} className="font-inter">
          Options - {outfitName}
        </Text>
        <button
          type="button"
          onClick={() => {
            if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
              ref.current.hide();
            }
          }}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <Text size="small" color="black" className="font-inter">
            Close
          </Text>
          <HiX size={20} />
        </button>
      </div>
    );

    return (
      <>
        <Modal
          ref={ref}
          size="xl"
          headerComponent={headerComponent}
          showFooter={false}
          showCloseIcon={false}
        >
          <div className="flex flex-col h-full min-h-[400px]">
            <div className="mb-4">
              <Text className="font-inter text-sm text-gray-600 mb-4 block mt-1" color="black">
                For each piece, pick measurements from Darzee&apos;s library (with reference images)
                or add custom fields with your own images.
              </Text>

              {outfitPieces.length > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => handleNavigatePiece('prev')}
                    disabled={outfitPieces.findIndex((p) => p.id === selectedPieceId) <= 0}
                    className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <HiChevronLeft size={18} />
                  </button>
                  <button
                    type="button"
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 min-w-[200px]"
                  >
                    {currentPiece?.outfit_link ? (
                      <img
                        src={currentPiece.outfit_link}
                        alt={currentPiece.name}
                        className="w-8 h-8 rounded-lg object-cover"
                      />
                    ) : (
                      <span className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-gray-500 text-xs">
                        <BsGrid3X3 size={14} />
                      </span>
                    )}
                    <Text className="font-inter text-sm truncate" color="black">
                      {currentPiece?.name ?? 'Select piece'}
                    </Text>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleNavigatePiece('next')}
                    disabled={
                      outfitPieces.findIndex((p) => p.id === selectedPieceId) >=
                      outfitPieces.length - 1
                    }
                    className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <HiChevronRight size={18} />
                  </button>
                </div>
              )}

              {showHeaderHint && (
                <Text className="font-inter text-xs text-gray-500 mb-2 block" color="black">
                  {headerHint}
                </Text>
              )}
            </div>

            <div className="flex-1 overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:hover:bg-gray-500">
              {isLoading ? (
                <div className="flex justify-center items-center h-[200px]">
                  <Loader />
                </div>
              ) : options.length > 0 ? (
                <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={optionIds} strategy={verticalListSortingStrategy}>
                    <div className={`space-y-4 ${dndWrapperClassName}`}>
                      {options.map((option) => (
                        <SortableOptionItem
                          key={option.id}
                          option={option}
                          onModify={handleModifyOption}
                          onDelete={handleDeleteOption}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className="flex flex-col items-center justify-center h-[200px] p-5">
                  <Text color="gray" size="small" className="font-inter">
                    No options configured. Add your first option below.
                  </Text>
                </div>
              )}
            </div>

            <div className="border-t border-gray-300 pt-4 pb-1">
              <Button
                label="+ Add Option"
                size="medium"
                bgColor="primary"
                onClick={handleAddOption}
                className="w-full"
                disabled={!selectedPieceId}
              />
            </div>
          </div>
        </Modal>

        <SelectInputTypeModal ref={selectInputTypeModalRef} onSelectType={handleSelectInputType} />

        <SingleSelectOptionModal
          ref={singleSelectModalRef}
          atomicOutfitId={selectedPieceId}
          existingOption={optionToEdit?.type === 'RADIO' ? optionToEdit : null}
          onSaved={handleOptionSaved}
          onClose={() => setOptionToEdit(null)}
        />

        <MultiSelectOptionModal
          ref={multiSelectModalRef}
          atomicOutfitId={selectedPieceId}
          existingOption={optionToEdit?.type === 'MULTI_SELECT' ? optionToEdit : null}
          onSaved={handleOptionSaved}
          onClose={() => setOptionToEdit(null)}
        />

        <NumberOptionModal
          ref={numberModalRef}
          atomicOutfitId={selectedPieceId}
          existingOption={optionToEdit?.type === 'COUNTER' ? optionToEdit : null}
          onSaved={handleOptionSaved}
          onClose={() => setOptionToEdit(null)}
        />

        <MultiLineOptionModal
          ref={multiLineModalRef}
          atomicOutfitId={selectedPieceId}
          existingOption={optionToEdit?.type === 'MULTI_LINE_INPUT' ? optionToEdit : null}
          onSaved={handleOptionSaved}
          onClose={() => setOptionToEdit(null)}
        />
      </>
    );
  }
);

OptionsModal.displayName = 'OptionsModal';

export default OptionsModal;
